import { useId } from "react";
import { FileText, ArrowRight, ImagePlus, Pencil } from "lucide-react";

interface TextInputSectionProps {
	textInput: string;
	sceneCount: number;
	/** false かつ lg 未満のときは台本欄を畳んで要約行だけを出す */
	scriptOpen: boolean;
	onTextChange: (text: string) => void;
	onApplyTexts: () => void;
	onAddImages: () => void;
	onToggleScript: (open: boolean) => void;
}

export function TextInputSection({
	textInput,
	sceneCount,
	scriptOpen,
	onTextChange,
	onApplyTexts,
	onAddImages,
	onToggleScript,
}: TextInputSectionProps) {
	// 開閉ボタンと台本欄を aria-controls で結ぶための ID
	const scriptId = useId();
	const lineCount = textInput.split("\n").filter((l) => l.trim()).length;

	const isMatched = lineCount === sceneCount && sceneCount > 0;
	const hasMismatch =
		lineCount > 0 && sceneCount > 0 && lineCount !== sceneCount;

	/* 行数と画像枚数。畳んでいるときも開いているときも同じ並びで出す */
	const countRow = (
		<div className="flex items-center gap-3">
			<div className="flex items-center gap-1.5 text-sm">
				<FileText size={14} className="text-warm-400" />
				<span
					className={`font-medium ${
						hasMismatch ? "text-amber-600" : "text-warm-600"
					}`}
				>
					{lineCount}行
				</span>
				<ArrowRight size={12} className="text-warm-300" />
				<span
					className={`font-medium ${
						isMatched ? "text-teal-600" : "text-warm-600"
					}`}
				>
					{sceneCount}画像
				</span>
			</div>
			{isMatched && <span className="badge badge-success">一致</span>}
			{hasMismatch && <span className="badge badge-warning">不一致</span>}
		</div>
	);

	return (
		<div className="space-y-3">
			{/* 開閉に関わらず動かないヘッダー行。畳んでも上端の位置が変わらない */}
			<div className="flex items-center justify-between gap-2 lg:hidden">
				{countRow}
				{/* 開閉のどちらでも同じ位置・同じ形。畳んでも上端が動かない */}
				<button
					onClick={() => onToggleScript(!scriptOpen)}
					aria-expanded={scriptOpen}
					aria-controls={scriptId}
					aria-label={scriptOpen ? "台本の入力を閉じる" : "台本を編集"}
					className="btn-icon shrink-0"
				>
					<Pencil size={18} />
				</button>
			</div>

			{/* 台本欄。lg 未満では高さを 0fr <-> 1fr で繋いで畳む */}
			<div
				id={scriptId}
				className={`collapsible ${scriptOpen ? "collapsible-open" : ""}`}
			>
				<div>
					<div className="space-y-3">
						<textarea
							value={textInput}
							onChange={(e) => onTextChange(e.target.value)}
							className="textarea-base h-24 lg:h-40"
							placeholder="各シーンのテキストを改行区切りで入力します"
						/>

						<div className="flex flex-wrap items-center justify-between gap-2">
							{/* lg では従来どおりここに行数を出す */}
							<div className="hidden lg:block">{countRow}</div>

							<div className="flex items-center gap-2">
								{/* 画像追加はスマートフォン幅ではこの入力カードに置く。
								    デスクトップではツールバー側の同名ボタンが担う。
								    components.css のボタンは display を持つので、
								    表示切り替えは素の div に寄せる */}
								<div className="lg:hidden">
									<button
										onClick={onAddImages}
										className="btn-tertiary"
									>
										<ImagePlus size={16} />
										画像追加
									</button>
								</div>
								<button
									onClick={onApplyTexts}
									disabled={sceneCount === 0 || lineCount === 0}
									className="btn-secondary"
								>
									<ArrowRight size={16} />
									適用
								</button>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
}
